import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Ordinary cart lines can step down to zero for removal. Logo lines stop at their purchase minimum.
   * Positive quantities still follow the variant's minimum and increment.
   * @param {number} stepMultiplier - Positive for increase, negative for decrease
   */
  updateQuantity(stepMultiplier) {
    const { quantityInput } = this.refs;
    const { min, value } = this.getCurrentValues();

    if (!this.hasPurchaseMinimum && stepMultiplier < 0 && value <= min) {
      quantityInput.value = '0';
      this.onQuantityChange();
      this.updateButtonStates();
      return;
    }

    super.updateQuantity(stepMultiplier);
  }

  /**
   * Allows a manually entered zero to remove ordinary cart lines.
   * @param {Event} event - The input blur event
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    if (!this.hasPurchaseMinimum && event.target.value.trim() !== '' && Number(event.target.value) === 0) {
      event.preventDefault();
      this.refs.quantityInput.value = '0';
      this.onQuantityChange();
      this.updateButtonStates();
      return;
    }

    super.setQuantity(event);
  }

  /** Logo lines must retain their purchase minimum; Remove remains a separate cart action. */
  get hasPurchaseMinimum() {
    return Number(this.dataset.minimumQuantity) > 0;
  }

  /** Validate the final quantity before a cart update event can be sent. */
  onQuantityChange() {
    const { min, max, step, value } = this.getCurrentValues();
    const quantity = Number(this.refs.quantityInput.value);
    const isRemoval = quantity === 0 && !this.hasPurchaseMinimum;
    const isValid = Number.isSafeInteger(quantity) && value >= min && (value - min) % step === 0 &&
      (max === null || value <= max);

    if (!isRemoval && !isValid) {
      this.refs.quantityInput.reportValidity();
      this.updateButtonStates();
      return;
    }

    super.onQuantityChange();
  }

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Preserve server restrictions while applying the cart's absolute limits.
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    const disabled = this.refs.quantityInput.disabled;
    minusButton.disabled = disabled || this.serverDisabledMinus || value <= (this.hasPurchaseMinimum ? min : 0);
    plusButton.disabled = disabled || this.serverDisabledPlus || (effectiveMax !== null && value >= effectiveMax);
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
